import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TradingInterface = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('1D');
  const [orderType, setOrderType] = useState('market');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [isConnected, setIsConnected] = useState(true);

  const [marketData, setMarketData] = useState({
    currentPrice: 85.42,
    change: 2.15,
    changePercent: 2.58,
    volume: 125840,
    high24h: 87.20,
    low24h: 82.10,
    lastUpdate: new Date()
  });

  const [orderBook, setOrderBook] = useState({
    bids: [
      { price: 85.40, quantity: 1250, total: 106750 },
      { price: 85.35, quantity: 890, total: 75962 },
      { price: 85.30, quantity: 2100, total: 179130 },
      { price: 85.25, quantity: 750, total: 63938 },
      { price: 85.20, quantity: 1800, total: 153360 }
    ],
    asks: [
      { price: 85.45, quantity: 980, total: 83741 },
      { price: 85.50, quantity: 1450, total: 123975 },
      { price: 85.55, quantity: 720, total: 61596 },
      { price: 85.60, quantity: 1200, total: 102720 },
      { price: 85.65, quantity: 850, total: 72803 }
    ]
  });

  const timeframes = ['1H', '4H', '1D', '1W', '1M'];

  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate real-time price updates
      setMarketData(prev => ({
        ...prev,
        currentPrice: prev?.currentPrice + (Math.random() - 0.5) * 0.5,
        change: prev?.change + (Math.random() - 0.5) * 0.1,
        lastUpdate: new Date()
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const handlePlaceOrder = () => {
    if (!quantity || (orderType === 'limit' && !price)) {
      return;
    }
    
    console.log('Placing order:', {
      type: orderType,
      quantity: parseInt(quantity),
      price: orderType === 'limit' ? parseFloat(price) : marketData?.currentPrice
    });
    
    // Reset form
    setQuantity('');
    setPrice('');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    })?.format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US')?.format(value);
  };

  return (
    <div className="bg-card rounded-lg border shadow-maritime-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-semibold text-foreground">EUA Trading</h2>
          <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium ${
            isConnected 
              ? 'bg-success/10 text-success' :'bg-error/10 text-error'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              isConnected ? 'bg-success' : 'bg-error'
            }`} />
            <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {timeframes?.map((tf) => (
            <button
              key={tf}
              onClick={() => setSelectedTimeframe(tf)}
              className={`px-3 py-1 rounded text-sm font-medium transition-maritime ${
                selectedTimeframe === tf
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {tf}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
        {/* Price Chart Area */}
        <div className="lg:col-span-2">
          <div className="mb-4">
            <div className="flex items-center space-x-4 mb-2">
              <div className="text-3xl font-bold text-foreground">
                {formatCurrency(marketData?.currentPrice)}
              </div>
              <div className={`flex items-center space-x-1 text-sm font-medium ${
                marketData?.change >= 0 ? 'text-success' : 'text-error'
              }`}>
                <Icon 
                  name={marketData?.change >= 0 ? 'TrendingUp' : 'TrendingDown'} 
                  size={16} 
                  strokeWidth={2} 
                />
                <span>{marketData?.change >= 0 ? '+' : ''}{marketData?.change?.toFixed(2)}</span>
                <span>({marketData?.changePercent >= 0 ? '+' : ''}{marketData?.changePercent?.toFixed(2)}%)</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-muted-foreground">
              <span>Vol: {formatNumber(marketData?.volume)}</span>
              <span>High: {formatCurrency(marketData?.high24h)}</span>
              <span>Low: {formatCurrency(marketData?.low24h)}</span>
              <span>Updated: {marketData?.lastUpdate?.toLocaleTimeString()}</span>
            </div>
          </div>

          {/* Chart Placeholder */}
          <div className="h-80 bg-muted rounded-lg flex items-center justify-center">
            <div className="text-center">
              <Icon name="TrendingUp" size={48} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">EUA Price Chart</p>
              <p className="text-sm text-muted-foreground">Real-time market data visualization</p>
            </div>
          </div>
        </div>

        {/* Order Book & Trading Panel */}
        <div className="space-y-6">
          {/* Order Book */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Order Book</h3>
            
            <div className="space-y-2 mb-4">
              <div className="text-xs text-muted-foreground grid grid-cols-3 gap-2 px-2">
                <span>Price (€)</span>
                <span className="text-right">Size</span>
                <span className="text-right">Total</span>
              </div>
              
              {/* Asks */}
              {orderBook?.asks?.reverse()?.map((ask, index) => (
                <div key={`ask-${index}`} className="grid grid-cols-3 gap-2 text-xs px-2 py-1 hover:bg-error/5 rounded">
                  <span className="text-error font-mono">{ask?.price?.toFixed(2)}</span>
                  <span className="text-right font-mono">{formatNumber(ask?.quantity)}</span>
                  <span className="text-right font-mono text-muted-foreground">{formatNumber(ask?.total)}</span>
                </div>
              ))}
              
              {/* Spread */}
              <div className="border-t border-b border-border py-2 text-center">
                <span className="text-xs text-muted-foreground">
                  Spread: {formatCurrency(orderBook?.asks?.[0]?.price - orderBook?.bids?.[0]?.price || 0)}
                </span>
              </div>
              
              {/* Bids */}
              {orderBook?.bids?.map((bid, index) => (
                <div key={`bid-${index}`} className="grid grid-cols-3 gap-2 text-xs px-2 py-1 hover:bg-success/5 rounded">
                  <span className="text-success font-mono">{bid?.price?.toFixed(2)}</span>
                  <span className="text-right font-mono">{formatNumber(bid?.quantity)}</span>
                  <span className="text-right font-mono text-muted-foreground">{formatNumber(bid?.total)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Trading Panel */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Place Order</h3>
            
            <div className="space-y-4">
              {/* Order Type */}
              <div className="flex space-x-2">
                <button
                  onClick={() => setOrderType('market')}
                  className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-maritime ${
                    orderType === 'market' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Market
                </button>
                <button
                  onClick={() => setOrderType('limit')}
                  className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-maritime ${
                    orderType === 'limit' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Limit
                </button>
              </div>

              {/* Quantity Input */}
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">
                  Quantity (EUA)
                </label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e?.target?.value)}
                  placeholder="Enter quantity"
                  className="w-full px-3 py-2 bg-background border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              {/* Price Input (for limit orders) */}
              {orderType === 'limit' && (
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">
                    Price (€)
                  </label>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e?.target?.value)}
                    placeholder="Enter price"
                    step="0.01"
                    className="w-full px-3 py-2 bg-background border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              )}

              {/* Order Summary */}
              {quantity && (
                <div className="bg-background rounded p-3 text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Estimated Total:</span>
                    <span className="font-medium">
                      {formatCurrency(
                        parseInt(quantity || 0) * 
                        (orderType === 'limit' ? parseFloat(price || 0) : marketData?.currentPrice)
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Commission:</span>
                    <span className="font-medium">€2.50</span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={handlePlaceOrder}
                  disabled={!quantity || (orderType === 'limit' && !price)}
                  className="bg-success text-success-foreground hover:bg-success/90"
                >
                  <Icon name="TrendingUp" size={16} strokeWidth={2} className="mr-2" />
                  Buy
                </Button>
                <Button
                  onClick={handlePlaceOrder}
                  disabled={!quantity || (orderType === 'limit' && !price)}
                  className="bg-error text-error-foreground hover:bg-error/90"
                >
                  <Icon name="TrendingDown" size={16} strokeWidth={2} className="mr-2" />
                  Sell
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingInterface;